import { addMinutes, isBefore, isValid, parseISO } from "date-fns";

export const GUEST_APPLICANT_SIGNUP_CONTEXT_KEY =
	"guest_applicant_signup_context_v1";

const GUEST_APPLICANT_SIGNUP_TTL_MINUTES = 30;

export type GuestApplicantSignupContextInput = {
	email: string;
	jobId: string;
	jobTitle: string;
	jobLocation: string;
};

export type GuestApplicantSignupContext = GuestApplicantSignupContextInput & {
	expiresAt: string;
};

export type GuestApplicantSignupContextReadResult =
	| {
			status: "valid";
			context: GuestApplicantSignupContext;
	  }
	| {
			status: "missing" | "expired" | "invalid";
			context: null;
	  };

const normalizeValue = (value: string) => value.trim();

const normalizeEmail = (email: string) => normalizeValue(email).toLowerCase();

const isNonEmptyString = (value: unknown): value is string =>
	typeof value === "string" && value.trim().length > 0;

const isGuestApplicantSignupContext = (
	value: unknown
): value is GuestApplicantSignupContext => {
	if (!value || typeof value !== "object") {
		return false;
	}

	const context = value as Record<string, unknown>;

	return (
		isNonEmptyString(context.email) &&
		isNonEmptyString(context.jobId) &&
		isNonEmptyString(context.jobTitle) &&
		isNonEmptyString(context.jobLocation) &&
		isNonEmptyString(context.expiresAt)
	);
};

const buildGuestApplicantSignupContext = (
	input: GuestApplicantSignupContextInput
): GuestApplicantSignupContext => ({
	email: normalizeEmail(input.email),
	jobId: normalizeValue(input.jobId),
	jobTitle: normalizeValue(input.jobTitle),
	jobLocation: normalizeValue(input.jobLocation),
	expiresAt: addMinutes(
		new Date(),
		GUEST_APPLICANT_SIGNUP_TTL_MINUTES
	).toISOString(),
});

export const clearGuestApplicantSignupContext = () => {
	window.localStorage.removeItem(GUEST_APPLICANT_SIGNUP_CONTEXT_KEY);
};

export const storeGuestApplicantSignupContext = (
	input: GuestApplicantSignupContextInput
) => {
	const context = buildGuestApplicantSignupContext(input);

	window.localStorage.setItem(
		GUEST_APPLICANT_SIGNUP_CONTEXT_KEY,
		JSON.stringify(context)
	);

	return context;
};

export const readGuestApplicantSignupContext =
	(): GuestApplicantSignupContextReadResult => {
		const rawContext = window.localStorage.getItem(
			GUEST_APPLICANT_SIGNUP_CONTEXT_KEY
		);

		if (!rawContext) {
			return {
				status: "missing",
				context: null,
			};
		}

		try {
			const parsedContext = JSON.parse(rawContext);

			if (!isGuestApplicantSignupContext(parsedContext)) {
				clearGuestApplicantSignupContext();

				return {
					status: "invalid",
					context: null,
				};
			}

			const expiresAt = parseISO(parsedContext.expiresAt);

			if (!isValid(expiresAt)) {
				clearGuestApplicantSignupContext();

				return {
					status: "invalid",
					context: null,
				};
			}

			if (isBefore(expiresAt, new Date())) {
				clearGuestApplicantSignupContext();

				return {
					status: "expired",
					context: null,
				};
			}

			return {
				status: "valid",
				context: {
					email: normalizeEmail(parsedContext.email),
					jobId: normalizeValue(parsedContext.jobId),
					jobTitle: normalizeValue(parsedContext.jobTitle),
					jobLocation: normalizeValue(parsedContext.jobLocation),
					expiresAt: parsedContext.expiresAt,
				},
			};
		} catch {
			clearGuestApplicantSignupContext();

			return {
				status: "invalid",
				context: null,
			};
		}
	};

export const readGuestApplicantSignupContextFromSearch = (
	search: string
): GuestApplicantSignupContextInput | null => {
	const searchParams = new URLSearchParams(search);

	const email = searchParams.get("src_email");
	const jobId = searchParams.get("job_id");
	const jobTitle = searchParams.get("job_title");
	const jobLocation = searchParams.get("job_location");

	if (
		!isNonEmptyString(email) ||
		!isNonEmptyString(jobId) ||
		!isNonEmptyString(jobTitle) ||
		!isNonEmptyString(jobLocation)
	) {
		return null;
	}

	return {
		email: normalizeEmail(email),
		jobId: normalizeValue(jobId),
		jobTitle: normalizeValue(jobTitle),
		jobLocation: normalizeValue(jobLocation),
	};
};
